import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CsrIndicator } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { formatCsrStrategyUnitCode } from "src/utils/csrStrategyUnit";
import { getLeverValueColor } from "src/utils/csrStrategyValueColor";
import { hasRichContent } from "src/utils/richText";
import { LeverIndicatorCard } from "./LeverIndicatorCard/LeverIndicatorCard";
import { LeverIndicatorGoal } from "./LeverIndicatorCard/LeverIndicatorGoal";

type IndicatorListItemProps = {
    indicator: CsrIndicator;
    currentCampaignGoalLabel?: string;
    targetCampaignGoalLabel?: string;
    onOpenLeverDetails?: (indicator: CsrIndicator) => void;
};

type GetIndicatorGoalsParams = {
    indicator: CsrIndicator;
    unit: string;
    currentCampaignGoalLabel?: string;
    targetCampaignGoalLabel?: string;
    notDefinedLabel: string;
};

const getIndicatorGoals = (params: GetIndicatorGoalsParams): LeverIndicatorGoal[] => {
    const { indicator, unit, currentCampaignGoalLabel, targetCampaignGoalLabel, notDefinedLabel } = params;
    const createGoal = (
        label: string | undefined,
        target: typeof indicator.shortTermTarget,
    ): LeverIndicatorGoal | null => {
        if (!label) return null;

        return {
            label,
            value: getTargetValue(target, notDefinedLabel),
            unit: hasTargetValue(target) ? unit : "",
        };
    };

    return [
        createGoal(currentCampaignGoalLabel, indicator.shortTermTarget),
        createGoal(targetCampaignGoalLabel, indicator.longTermTarget),
    ].filter((goal): goal is LeverIndicatorGoal => goal !== null);
};

const hasTargetValue = (value?: string | null): boolean => {
    return Boolean(value?.trim());
};

const getTargetValue = (value: string | null | undefined, notDefinedLabel: string): string => {
    return hasTargetValue(value) ? (value ?? "") : notDefinedLabel;
};

export const IndicatorListItem = (props: IndicatorListItemProps) => {
    const { indicator, currentCampaignGoalLabel, targetCampaignGoalLabel, onOpenLeverDetails } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const unit = formatCsrStrategyUnitCode(indicator.unitCode);
    const notDefinedLabel = t("csr_strategy.page.goal.not_defined", "Not defined");
    const goals = getIndicatorGoals({
        indicator,
        unit,
        currentCampaignGoalLabel,
        targetCampaignGoalLabel,
        notDefinedLabel,
    });

    return (
        <Box
            sx={{
                display: "grid",
                gridRow: "span 4",
                gridTemplateRows: "subgrid",
                paddingBottom: 2,
            }}
        >
            <LeverIndicatorCard
                title={indicator.leverShortName || indicator.name}
                indicatorLabel={indicator.indicatorLabel || indicator.name}
                currentValue={indicator.currentValue}
                unit={unit}
                currentValueColor={getLeverValueColor(theme, indicator.valueColor)}
                goals={goals}
                canOpenDetails={hasRichContent(indicator.richContent)}
                openDetailsLabel={t("csr_strategy.lever_details.open", "Open lever details")}
                onOpenDetails={() => onOpenLeverDetails?.(indicator)}
            />
        </Box>
    );
};
